// verisoul.service.ts
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    Verisoul: any;
  }
}

@Injectable({ providedIn: 'root' })
export class VerisoulService {
  private readonly sdkScriptId = 'verisoul-sdk-script';
  private readonly bootstrapScriptId = 'verisoul-bootstrap-script';
  private scriptLoaded = false;
  private loadPromise: Promise<void> | null = null;

  private get enabled(): boolean {
    return environment.verisoul?.enabled ?? false;
  }

  private getScriptElement(projectId: string): HTMLScriptElement | null {
    return document.getElementById(this.sdkScriptId) as HTMLScriptElement
      || document.querySelector(`script[verisoul-project-id="${projectId}"]`) as HTMLScriptElement
      || null;
  }

  private ensureBootstrapScript(): void {
    if (document.getElementById(this.bootstrapScriptId)) {
      return;
    }

    const bootstrap = document.createElement('script');
    bootstrap.id = this.bootstrapScriptId;
    bootstrap.innerHTML = `!function(e){if(e.Verisoul)return;const r=[],t={},o=new Proxy(t,{get:(e,o)=>o in t?t[o]:(...e)=>new Promise(((t,n)=>r.push([o,e,t,n]))),set:(e,r,o)=>(t[r]=o,!0)});e.Verisoul=o;const n=()=>{Object.keys(t).length&&r.splice(0).forEach((([e,r,o,n])=>{try{Promise.resolve(t[e](...r)).then(o,n)}catch(e){n(e)}}))},c=document.querySelector("script[verisoul-project-id]"),s=()=>r.splice(0).forEach((([,,,e])=>e(new Error("Failed to load Verisoul SDK"))));if(!c)return void s();c.addEventListener("load",n,{once:!0}),c.addEventListener("error",(()=>{clearInterval(i),s()}),{once:!0});const i=setInterval((()=>{Object.keys(t).length&&(clearInterval(i),n())}),40)}(window);`;
    document.head.appendChild(bootstrap);
  }

  loadSdk(verisoulUrl: string, projectId: string): Promise<void> {
    if (!this.enabled) return Promise.resolve();

    const existingScript = this.getScriptElement(projectId);
    const isReady = !!window.Verisoul?.session || (existingScript && existingScript.dataset.loaded === 'true');

    if (isReady) {
      this.scriptLoaded = true;
      return Promise.resolve();
    }

    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      const script = existingScript || document.createElement('script');

      if (!existingScript) {
        script.id = this.sdkScriptId;
        script.src = verisoulUrl;
        script.async = true;
        script.setAttribute('verisoul-project-id', projectId);
      }

      const completeLoad = () => {
        script.dataset.loaded = 'true';
        this.scriptLoaded = true;
        resolve();
      };

      if (script.dataset.loaded === 'true' || !!window.Verisoul?.session) {
        completeLoad();
        return;
      }

      script.onload = () => {
        this.ensureBootstrapScript();
        completeLoad();
      };

      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Verisoul SDK'));
      };

      if (!existingScript) {
        document.head.appendChild(script);
      } else if (!document.head.contains(script)) {
        document.head.appendChild(script);
      }

      this.ensureBootstrapScript();
    });

    return this.loadPromise;
  }

  async session(...args: any[]): Promise<any> {
    if (!this.enabled) return { session_id: '' };

    if (!this.scriptLoaded || !window.Verisoul?.session) {
      await this.loadSdk(environment.verisoul?.apiUrl ?? '', environment.verisoul?.projectId ?? '');
    }

    if (!window.Verisoul?.session) {
      throw new Error('Verisoul SDK not ready');
    }

    return window.Verisoul.session(...args);
  }
}